/**
 * TODO: Need to find a better way to deal with the non-null-assertions (var!)
 * It is used to provide correct type inference for the queries as null
 * checks would change the return type of the queries.
 *
 * At the same time, the DS API should not be returning nulls either.
 */
/* eslint-disable @typescript-eslint/no-non-null-assertion */
import { useMemo } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';

import {
  DsAPI,
  DSApiUrl,
  PagedRequest,
  useDirectoryServices,
} from '@xemplo/directory-services';
import { useHttpClient } from '@xemplo/http';
import { DEFAULT_QUERY_PROPS } from '@xemplo/query-helpers';
import {
  Company,
  CompanyApiModel,
  HrConnect,
  Instance,
  Navigation,
  NavigationApiModel,
  Persona,
  PersonaTransfer,
  ServiceProviderApiModel,
  UserflowSignature,
} from '@xemplo/types';
import { useAppSettings } from '@xemplo/xemplo-app-settings';

import { mapNavData } from './ds-query.helper';
import {
  CommonQueryProps,
  CompaniesApiParams,
  QueryWithCodeProps,
  QueryWithIdProps,
  QueryWithIdsProps,
  QueryWithParamsProps,
  ServiceProvidersApiParams,
} from './use-ds-query.types';

const defaultProps = <TData>() => ({}) as CommonQueryProps<TData>;
const defaultPropsWithId = <TData>() => ({ id: undefined }) as QueryWithIdProps<TData>;
const defaultPropsWithIds = <TData>() => ({ ids: undefined }) as QueryWithIdsProps<TData>;
const defaultPropsWithCode = <TData>() =>
  ({ code: undefined }) as QueryWithCodeProps<TData>;

export const useGetPersonasQuery = (
  props?: QueryWithParamsProps<Persona[], PagedRequest>,
) => {
  const { authToken, queryProps, requestProps } = props ?? defaultProps<Persona[]>();
  const mergedQueryProps = { ...queryProps, ...DEFAULT_QUERY_PROPS };
  const enabled = useMemo(() => authToken != null, [authToken]);
  const directoryServices = useDirectoryServices({ authToken });

  return useQuery<Persona[]>({
    ...mergedQueryProps,
    queryKey: [DsAPI.personas, requestProps, enabled],
    queryFn: () => directoryServices.getPersonas(requestProps).then((res) => res.data),
    enabled,
  });
};

export const useGetInstancesQuery = (
  props?: QueryWithParamsProps<Instance[], PagedRequest>,
) => {
  const { authToken, queryProps, requestProps } = props ?? defaultProps<Instance[]>();
  const mergedQueryProps = { ...queryProps, ...DEFAULT_QUERY_PROPS };
  const enabled = useMemo(() => authToken != null, [authToken]);
  const directoryServices = useDirectoryServices({ authToken });

  return useQuery<Instance[]>({
    ...mergedQueryProps,
    queryKey: [DsAPI.instances, enabled, requestProps],
    queryFn: () => directoryServices.getInstances(requestProps).then((res) => res.data),
    enabled,
  });
};

export const useGetInstanceByIdQuery = (props: QueryWithIdProps<Instance>) => {
  const { authToken, id, queryProps, requestProps } =
    props ?? defaultPropsWithId<Instance>();
  const mergedQueryProps = { ...queryProps, ...DEFAULT_QUERY_PROPS };
  const enabled = useMemo(() => id != null && authToken != null, [id, authToken]);
  const directoryServices = useDirectoryServices({ authToken });

  return useQuery<Instance>({
    ...mergedQueryProps,
    queryKey: [DsAPI.instances, enabled, requestProps, id],
    queryFn: () =>
      directoryServices
        .getInstanceById({ ...requestProps, id: id! })
        .then((res) => res.data),
    enabled,
  });
};

export const useGetCompanyByIdQuery = (props: QueryWithIdProps<Company>) => {
  const { authToken, id, queryProps, requestProps } =
    props ?? defaultPropsWithId<Company>();
  const mergedQueryProps = { ...queryProps, ...DEFAULT_QUERY_PROPS };

  const enabled = useMemo(() => id != null && authToken != null, [id, authToken]);
  const directoryServices = useDirectoryServices({ authToken });

  return useQuery<Company>({
    ...mergedQueryProps,
    queryKey: [DsAPI.companies, id, requestProps, enabled],
    queryFn: () =>
      directoryServices
        .getCompanyById({ ...requestProps, id: id! })
        .then((res) => res.data),
    enabled,
  });
};

/**
 * @deprecated Fetches a v1 company list scoped to an instance id. Use
 * {@link useGetCompaniesQueryV2}, which reads the unfiltered DS V2 company list.
 */
export const useGetCompaniesByInstanceIdQuery = (props: QueryWithIdProps<Company[]>) => {
  const { authToken, id, queryProps, requestProps } =
    props ?? defaultPropsWithId<Company[]>();
  const mergedQueryProps = { ...queryProps, ...DEFAULT_QUERY_PROPS };

  const enabled = useMemo(() => id != null && authToken != null, [id, authToken]);
  const directoryServices = useDirectoryServices({ authToken });

  return useQuery<Company[]>({
    ...mergedQueryProps,
    queryKey: [DsAPI.companiesByInstanceId, id, requestProps, enabled],
    queryFn: () =>
      directoryServices
        .getCompaniesByInstanceId({ ...requestProps, id: id! })
        .then((res) => res.data),
    enabled,
  });
};

export const useGetCompaniesQuery = (props: QueryWithIdsProps<Company[]>) => {
  const { authToken, ids, queryProps, requestProps } =
    props ?? defaultPropsWithIds<Company[]>();
  const mergedQueryProps = { ...queryProps, ...DEFAULT_QUERY_PROPS };

  const enabled = useMemo(() => ids != null && authToken != null, [ids, authToken]);
  const directoryServices = useDirectoryServices({ authToken });

  return useQuery<Company[]>({
    ...mergedQueryProps,
    queryKey: [DsAPI.companies, requestProps, enabled, ids],
    queryFn: () => directoryServices.getCompanies({ ...requestProps, ids: ids! }),
    enabled,
  });
};

export const useGetNavigationQuery = (props?: CommonQueryProps<Navigation>) => {
  const { authToken, queryProps, requestProps } = props ?? defaultProps<Navigation>();
  const mergedQueryProps = { ...queryProps, ...DEFAULT_QUERY_PROPS };

  const enabled = useMemo(() => authToken != null, [authToken]);
  const directoryServices = useDirectoryServices({ authToken });

  return useQuery<Navigation>({
    ...mergedQueryProps,
    queryKey: [DsAPI.navigation, requestProps, enabled],
    queryFn: () =>
      directoryServices
        .getNavigation(requestProps)
        .then((res) => res.data)
        .then(mapNavData),
    enabled,
  });
};

/**
 * This is a new version of the useGetNavigationQuery that uses the new http client.
 *
 * NOTE: For reasons I don't remember, I removed the enabled flag based on
 * AuthToken. This means that this query will run even if the user is
 * not authenticated and it will throw an error.
 *
 * I'll keep this behaviour as default, so it's backwards compatible. However,
 * I'm adding the enabled prop, which should allow implementation to override
 * this behaviour and calculate the enabled value based on whatever conditions
 * is relevant for the use case.
 *
 * @param enabled - A flag to enable or disable the query. Defaults to true.
 * @default true
 */
export const useGetNavigationQueryV2 = (props?: { enabled: boolean }) => {
  const { enabled = true } = props ?? {};
  const { client } = useHttpClient();
  const { dsUrl } = useAppSettings();
  const url = DSApiUrl.getNavigationUrl(dsUrl);

  return useQuery({
    queryKey: [url],
    queryFn: ({ signal }) =>
      client.get<NavigationApiModel>(url, { signal }).then((res) => res.data),
    select: mapNavData,
    enabled,
  });
};

export const useGetUserflowSignatureQuery = (
  props?: QueryWithCodeProps<UserflowSignature>,
) => {
  const { authToken, code, queryProps } =
    props ?? defaultPropsWithCode<UserflowSignature>();
  const mergedQueryProps = { ...queryProps, ...DEFAULT_QUERY_PROPS };
  const directoryServices = useDirectoryServices({ authToken });

  return useQuery<UserflowSignature>({
    ...mergedQueryProps,
    queryKey: [DsAPI.userflow, code],
    queryFn: () =>
      directoryServices
        .getUserflowSignature({ code: code ?? '' })
        .then((res) => res.data),
    enabled: code != null,
  });
};

/**
 * Fetches the connect embedded URL for a given persona code.
 * This query is enabled only when a valid code (personaId) is provided.
 * The URL is loaded directly into an iframe to load the connect embedded content.
 * For more information, https://docs.workato.com/en/oem/workato-embedded
 * @param props
 * @returns embeded URL for the connect dashboard
 */
export const useGetConnectQuery = (props?: QueryWithCodeProps<HrConnect>) => {
  const { authToken, code } = props ?? defaultPropsWithCode<HrConnect>();
  const directoryServices = useDirectoryServices({ authToken });

  return useQuery<HrConnect>({
    queryKey: [DsAPI.embeddedLink, code, authToken],
    queryFn: () =>
      directoryServices
        .getConnectEmbeddedUrl({ code: code ?? '' })
        .then((res) => res.data),
    enabled: !!code,
  });
};

/** Invalidates the V2 navigation query so consumers refetch nav after mutations that change it. */
export const useInvalidateNavigationQueryV2 = () => {
  const queryClient = useQueryClient();
  const { dsUrl } = useAppSettings();
  const url = DSApiUrl.getNavigationUrl(dsUrl);

  return () => queryClient.invalidateQueries({ queryKey: [url] });
};

export const useGetPersonaTransferQuery = (props?: { enabled: boolean }) => {
  const { enabled = true } = props ?? {};
  const { client } = useHttpClient();
  const { dsUrl } = useAppSettings();
  const url = DSApiUrl.getCurrentUserTransferPersonas(dsUrl);

  return useQuery({
    queryKey: [url],
    queryFn: ({ signal }) =>
      client.get<PersonaTransfer[]>(url, { signal }).then((res) => res.data),
    enabled,
  });
};

/**
 * Fetches the unfiltered DS V2 company list from `GET /api/v2/companies`.
 * Pass `params` to narrow/sort/page server-side; omit for the full list.
 */
export const useGetCompaniesQueryV2 = (props?: {
  params?: CompaniesApiParams;
  enabled?: boolean;
}) => {
  const { params, enabled = true } = props ?? {};
  const { client } = useHttpClient();
  const { dsUrl } = useAppSettings();
  const url = DSApiUrl.getCompaniesV2Url(dsUrl);

  return useQuery({
    queryKey: [url, params],
    queryFn: ({ signal }) =>
      client.get<CompanyApiModel[]>(url, { signal, params }).then((res) => res.data),
    enabled,
  });
};

/**
 * Fetches the DS V2 service provider list from `GET /api/v2/service-providers`.
 * Pass `params` to narrow/sort/page server-side; omit for the full list.
 */
export const useGetServiceProvidersQueryV2 = (props?: {
  params?: ServiceProvidersApiParams;
  enabled?: boolean;
}) => {
  const { params, enabled = true } = props ?? {};
  const { client } = useHttpClient();
  const { dsUrl } = useAppSettings();
  const url = DSApiUrl.getServiceProvidersUrl(dsUrl);

  return useQuery({
    queryKey: [url, params],
    queryFn: ({ signal }) =>
      client
        .get<ServiceProviderApiModel[]>(url, { signal, params })
        .then((res) => res.data),
    enabled,
  });
};
